import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { formatDateTime } from '../utils/format';
import AutoRefreshControls from '../components/AutoRefreshControls';
import ModalShell from '../components/users/ModalShell';

function formatUptime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '-';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatBytesPerSec(bps) {
  if (!Number.isFinite(bps) || bps <= 0) return '0 B/s';
  const units = ['B/s', 'kB/s', 'MB/s', 'GB/s'];
  let i = 0;
  let v = bps;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function pctColor(pct) {
  if (pct > 80) return 'bg-danger';
  if (pct > 50) return 'bg-warning';
  return 'bg-success';
}

function formatAgo(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '-';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

function StatusBadge({ status, health }) {
  let cls = 'bg-secondary';
  if (status === 'running') cls = health === 'unhealthy' ? 'bg-warning text-dark' : 'bg-success';
  else if (status === 'exited' || status === 'dead') cls = 'bg-danger';
  else if (status === 'restarting') cls = 'bg-warning text-dark';
  const label = health && status === 'running' && health !== 'none' ? `${status} / ${health}` : status;
  return <span className={`badge ${cls}`}>{label}</span>;
}

function SummaryCard({ title, value, color }) {
  return (
    <div className={`card text-white ${color}`}>
      <div className="card-body py-2">
        <div className="small text-uppercase opacity-75">{title}</div>
        <div className="fs-3 fw-bold">{value}</div>
      </div>
    </div>
  );
}

const CONTAINER_INTERVAL_OPTIONS = [10, 30, 60, 300];
const STALE_THRESHOLD_SECONDS = 7200; // 最後活動超過 2 小時視為可疑
const RESTART_WARN = 3;
const RESTART_DANGER = 10;

function RestartBadge({ count }) {
  if (!count) return <span>0</span>;
  if (count > RESTART_DANGER) return <span className="badge bg-danger">{count}</span>;
  if (count > RESTART_WARN) return <span className="badge bg-warning text-dark">{count}</span>;
  return <span>{count}</span>;
}

function VMSection({ vm }) {
  const { t } = useTranslation();
  const [expandCores, setExpandCores] = useState(false);
  const [expandDisks, setExpandDisks] = useState(false);

  if (!vm) {
    return (
      <div className="card mb-3">
        <div className="card-header fw-bold">{t('containerStatus.vm.title')}</div>
        <div className="card-body text-muted small">{t('common.noData')}</div>
      </div>
    );
  }

  const cpuPct = vm.cpu?.percent ?? 0;
  const memPct = vm.memory?.percent ?? 0;
  const swap = vm.memory?.swap;
  const primaryDisk = (vm.disk || []).find((d) => d.mount === '/') || (vm.disk || [])[0];
  const otherDisks = (vm.disk || []).filter((d) => d !== primaryDisk);
  const loadAvg = vm.cpu?.load_avg || [];

  return (
    <div className="card mb-3">
      <div className="card-header fw-bold d-flex justify-content-between align-items-center">
        <span>{t('containerStatus.vm.title')}</span>
        {vm.sampled_at && (
          <small className="text-muted fw-normal">
            {formatDateTime(new Date(vm.sampled_at * 1000).toISOString())}
          </small>
        )}
      </div>
      <div className="card-body">
        <div className="row g-3">
          <div className="col-12 col-md-6 col-xl-3">
            <div className="border rounded p-2 h-100">
              <div className="d-flex justify-content-between align-items-baseline">
                <span className="text-muted small">{t('containerStatus.vm.cpu')}</span>
                <span className="text-muted small">{vm.cpu?.count} cores</span>
              </div>
              <div className="fs-3 fw-bold mb-1">{cpuPct.toFixed(1)}%</div>
              <div className="progress" style={{ height: 8 }}>
                <div className={`progress-bar ${pctColor(cpuPct)}`} style={{ width: `${Math.min(cpuPct, 100)}%` }} />
              </div>
              {loadAvg.length === 3 && (
                <div className="text-muted small mt-1">
                  {t('containerStatus.vm.loadAvg')} {loadAvg.map((v) => v.toFixed(2)).join(' / ')}
                </div>
              )}
              {(vm.cpu?.per_core || []).length > 0 && (
                <>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 mt-1"
                    onClick={() => setExpandCores((v) => !v)}
                  >
                    {expandCores ? t('containerStatus.vm.collapse') : `${t('containerStatus.vm.perCore')} ▾`}
                  </button>
                  {expandCores && (
                    <div className="d-flex flex-wrap gap-1 mt-1">
                      {vm.cpu.per_core.map((p, i) => (
                        <div key={i} className="text-center" style={{ minWidth: 44 }}>
                          <div className="progress" style={{ height: 4 }}>
                            <div className={`progress-bar ${pctColor(p)}`} style={{ width: `${Math.min(p, 100)}%` }} />
                          </div>
                          <small className="text-muted" style={{ fontSize: 10 }}>{p.toFixed(0)}%</small>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="border rounded p-2 h-100">
              <div className="d-flex justify-content-between align-items-baseline">
                <span className="text-muted small">{t('containerStatus.vm.ram')}</span>
                <span className="text-muted small">
                  {(vm.memory?.used_mb / 1024).toFixed(1)} / {(vm.memory?.total_mb / 1024).toFixed(1)} GB
                </span>
              </div>
              <div className="fs-3 fw-bold mb-1">{memPct.toFixed(1)}%</div>
              <div className="progress" style={{ height: 8 }}>
                <div className={`progress-bar ${pctColor(memPct)}`} style={{ width: `${Math.min(memPct, 100)}%` }} />
              </div>
              {swap && swap.total_mb > 0 && (
                <div className="text-muted small mt-1">
                  {t('containerStatus.vm.swap')} {swap.used_mb.toFixed(0)} / {swap.total_mb.toFixed(0)} MB ({swap.percent}%)
                </div>
              )}
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="border rounded p-2 h-100">
              <div className="d-flex justify-content-between align-items-baseline">
                <span className="text-muted small">{t('containerStatus.vm.disk')}</span>
                {primaryDisk && (
                  <span className="text-muted small">
                    {primaryDisk.used_gb} / {primaryDisk.total_gb} GB
                  </span>
                )}
              </div>
              <div className="fs-3 fw-bold mb-1">{primaryDisk ? `${primaryDisk.percent}%` : '-'}</div>
              <div className="progress" style={{ height: 8 }}>
                <div
                  className={`progress-bar ${pctColor(primaryDisk?.percent ?? 0)}`}
                  style={{ width: `${Math.min(primaryDisk?.percent ?? 0, 100)}%` }}
                />
              </div>
              <div className="text-muted small mt-1">{primaryDisk?.mount || '/'}</div>
              {otherDisks.length > 0 && (
                <>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 mt-1"
                    onClick={() => setExpandDisks((v) => !v)}
                  >
                    {expandDisks ? t('containerStatus.vm.collapse') : `+${otherDisks.length} ▾`}
                  </button>
                  {expandDisks && (
                    <ul className="list-unstyled small mt-1 mb-0">
                      {otherDisks.map((d) => (
                        <li key={d.mount} className="d-flex justify-content-between">
                          <span className="text-muted">{d.mount}</span>
                          <span>
                            {d.used_gb}/{d.total_gb} GB ({d.percent}%)
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div className="border rounded p-2 h-100">
              <div className="text-muted small">{t('containerStatus.vm.network')} / {t('containerStatus.vm.io')}</div>
              <div className="small mt-1">
                <div className="d-flex justify-content-between">
                  <span className="text-muted">↓ {t('containerStatus.vm.rx')}</span>
                  <span>{formatBytesPerSec(vm.net?.rx_bytes_per_sec)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">↑ {t('containerStatus.vm.tx')}</span>
                  <span>{formatBytesPerSec(vm.net?.tx_bytes_per_sec)}</span>
                </div>
                <hr className="my-1" />
                <div className="d-flex justify-content-between">
                  <span className="text-muted">{t('containerStatus.vm.read')}</span>
                  <span>{formatBytesPerSec(vm.io?.read_bytes_per_sec)}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">{t('containerStatus.vm.write')}</span>
                  <span>{formatBytesPerSec(vm.io?.write_bytes_per_sec)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContainerStatus({ token, backendUrl }) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const abortRef = useRef(null);
  const [restartTarget, setRestartTarget] = useState(null); // { name, service }
  const [restartBusy, setRestartBusy] = useState(false);
  const [toast, setToast] = useState(null); // { type: 'success'|'danger', msg }

  const fetchSummary = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const res = await axios.get(`${backendUrl}/monitoring/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
        signal: controller.signal,
      });
      setSummary(res.data);
      setErr(null);
    } catch (e) {
      if (axios.isCancel(e) || e.name === 'CanceledError') return;
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, token]);

  const autoRefresh = useAutoRefresh(fetchSummary, {
    defaultInterval: 30,
    defaultEnabled: true,
    intervalOptions: CONTAINER_INTERVAL_OPTIONS,
  });

  // mount 時立即抓一次（auto-refresh hook 不會在 mount 觸發第一次）
  useEffect(() => {
    if (!token) return;
    fetchSummary();
    autoRefresh.setLastUpdated(new Date());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const stats = summary?.stats || { total: 0, running: 0, stopped: 0, unhealthy: 0 };
  const containers = summary?.containers || [];
  const vm = summary?.vm;
  const selfServiceName = summary?.self_service_name || 'fastapi';
  const scrapersLastRun = summary?.scrapers_last_run || [];
  const tasksSummary = summary?.tasks_summary || { by_scraper: [], totals: { success: 0, failed: 0, total: 0 } };

  const handleRestart = useCallback(async () => {
    if (!restartTarget) return;
    setRestartBusy(true);
    try {
      await axios.post(
        `${backendUrl}/monitoring/containers/${encodeURIComponent(restartTarget.name)}/restart`,
        null,
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true },
      );
      setToast({
        type: 'success',
        msg: t('containerStatus.restartSuccess', { service: restartTarget.service }),
      });
      setRestartTarget(null);
      // 給 docker 一點時間 stop+start，再 refresh
      setTimeout(() => fetchSummary(), 1500);
    } catch (e) {
      setToast({
        type: 'danger',
        msg: t('containerStatus.restartFailed', { error: e.response?.data?.detail || e.message }),
      });
    } finally {
      setRestartBusy(false);
    }
  }, [restartTarget, backendUrl, token, t, fetchSummary]);

  useEffect(() => {
    if (!toast) return undefined;
    const id = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(id);
  }, [toast]);

  const sortedContainers = useMemo(
    () => [...containers].sort((a, b) => (a.service || '').localeCompare(b.service || '')),
    [containers],
  );

  const highRestartCount = useMemo(
    () => containers.filter((c) => (c.restart_count || 0) > RESTART_DANGER).length,
    [containers],
  );

  return (
    <div className="p-3">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <h2 className="mb-0 me-auto">
          <i className="bi bi-hdd-stack me-2"></i>{t('containerStatus.title')}
          {loading && <span className="ms-2 spinner-border spinner-border-sm" />}
        </h2>
        <AutoRefreshControls {...autoRefresh} />
      </div>

      {err && (
        <div className="alert alert-danger py-2 small" role="alert">
          {err}
        </div>
      )}

      {toast && (
        <div className={`alert alert-${toast.type} py-2 small`} role="alert">
          {toast.msg}
        </div>
      )}

      <VMSection vm={vm} />

      <div className="row g-3 mb-3">
        <div className="col-6 col-md">
          <SummaryCard title={t('containerStatus.total')} value={stats.total} color="bg-secondary" />
        </div>
        <div className="col-6 col-md">
          <SummaryCard title={t('containerStatus.running')} value={stats.running} color="bg-success" />
        </div>
        <div className="col-6 col-md">
          <SummaryCard title={t('containerStatus.stopped')} value={stats.stopped} color="bg-danger" />
        </div>
        <div className="col-6 col-md">
          <SummaryCard title={t('containerStatus.unhealthy')} value={stats.unhealthy} color="bg-warning" />
        </div>
        <div className="col-6 col-md">
          <SummaryCard
            title={t('containerStatus.highRestart')}
            value={highRestartCount}
            color={highRestartCount > 0 ? 'bg-danger' : 'bg-secondary'}
          />
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-header fw-bold">{t('containerStatus.containersSection')}</div>
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0 align-middle">
            <thead>
              <tr>
                <th>{t('containerStatus.service')}</th>
                <th>{t('common.status')}</th>
                <th style={{ width: 180 }}>{t('containerStatus.cpu')}</th>
                <th style={{ width: 200 }}>{t('containerStatus.memory')}</th>
                <th>{t('containerStatus.uptime')}</th>
                <th className="text-end">{t('containerStatus.restarts')}</th>
                <th>{t('containerStatus.image')}</th>
                <th style={{ width: 90 }} className="text-end">{t('containerStatus.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedContainers.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-3">
                    {loading ? t('common.loading') : t('common.noData')}
                  </td>
                </tr>
              )}
              {sortedContainers.map((c) => {
                const cpu = c.cpu_percent || 0;
                const memPct = c.mem_percent || 0;
                const cpuBar = Math.min(cpu, 100);
                return (
                  <tr key={c.name}>
                    <td>
                      <div className="fw-semibold">{c.service}</div>
                      <div className="text-muted small">{c.name}</div>
                    </td>
                    <td><StatusBadge status={c.status} health={c.health} /></td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="progress flex-grow-1" style={{ height: 8 }}>
                          <div
                            className={`progress-bar ${cpu > 80 ? 'bg-danger' : cpu > 50 ? 'bg-warning' : 'bg-success'}`}
                            style={{ width: `${cpuBar}%` }}
                          />
                        </div>
                        <small className="text-nowrap">{cpu.toFixed(1)}%</small>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <div className="progress flex-grow-1" style={{ height: 8 }}>
                          <div
                            className={`progress-bar ${memPct > 80 ? 'bg-danger' : memPct > 50 ? 'bg-warning' : 'bg-info'}`}
                            style={{ width: `${Math.min(memPct, 100)}%` }}
                          />
                        </div>
                        <small className="text-nowrap">
                          {c.mem_usage_mb}/{c.mem_limit_mb} MB
                        </small>
                      </div>
                    </td>
                    <td className="small">{formatUptime(c.uptime_seconds)}</td>
                    <td className="text-end">
                      <RestartBadge count={c.restart_count} />
                    </td>
                    <td className="small text-muted">{c.image}</td>
                    <td className="text-end">
                      {c.service === selfServiceName ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled
                          title={t('containerStatus.cannotRestartSelf')}
                          aria-label={t('containerStatus.cannotRestartSelf')}
                        >
                          <i className="bi bi-arrow-clockwise" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-warning"
                          onClick={() => setRestartTarget({ name: c.name, service: c.service })}
                          title={t('containerStatus.restart')}
                          aria-label={t('containerStatus.restart')}
                        >
                          <i className="bi bi-arrow-clockwise" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header fw-bold">{t('containerStatus.lastRunSection')}</div>
            <ul className="list-group list-group-flush">
              {scrapersLastRun.length === 0 && (
                <li className="list-group-item text-muted text-center">
                  {t('common.noData')}
                </li>
              )}
              {scrapersLastRun.map((r) => {
                const stale = r.seconds_ago != null && r.seconds_ago > STALE_THRESHOLD_SECONDS;
                return (
                  <li key={r.service} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-semibold">{r.service}</div>
                      <small className="text-muted">{r.last_run ? formatDateTime(r.last_run) : t('containerStatus.never')}</small>
                    </div>
                    <span className={`badge ${stale ? 'bg-danger' : 'bg-secondary'}`}>
                      {r.seconds_ago != null ? formatAgo(r.seconds_ago) : '-'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="card h-100">
            <div className="card-header fw-bold">
              {t('containerStatus.taskSummarySection')}
              {tasksSummary.date && <small className="text-muted ms-2">({tasksSummary.date} UTC)</small>}
            </div>
            <div className="table-responsive">
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>{t('containerStatus.source')}</th>
                    <th className="text-end">{t('containerStatus.success')}</th>
                    <th className="text-end">{t('containerStatus.failed')}</th>
                    <th className="text-end">{t('containerStatus.total')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(tasksSummary.by_scraper || []).map((s) => (
                    <tr key={s.source}>
                      <td>{s.source}</td>
                      <td className="text-end text-success">{s.success}</td>
                      <td className={`text-end ${s.failed > 0 ? 'text-danger fw-semibold' : ''}`}>{s.failed}</td>
                      <td className="text-end">{s.total}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th>{t('containerStatus.totals')}</th>
                    <th className="text-end text-success">{tasksSummary.totals?.success || 0}</th>
                    <th className="text-end text-danger">{tasksSummary.totals?.failed || 0}</th>
                    <th className="text-end">{tasksSummary.totals?.total || 0}</th>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>

      {restartTarget && (
        <ModalShell
          title={t('containerStatus.restartConfirmTitle')}
          onClose={() => (!restartBusy ? setRestartTarget(null) : undefined)}
          footer={
            <>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setRestartTarget(null)}
                disabled={restartBusy}
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-warning"
                onClick={handleRestart}
                disabled={restartBusy}
              >
                {restartBusy ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {t('containerStatus.restarting')}
                  </>
                ) : (
                  t('containerStatus.restart')
                )}
              </button>
            </>
          }
        >
          <div className="modal-body">
            <p className="mb-0">
              {t('containerStatus.restartConfirmBody', {
                service: restartTarget.service,
                name: restartTarget.name,
              })}
            </p>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

export default ContainerStatus;
